<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <PlainImage
    v-if="fields.image?.id"
    :image="activeImage"
    :skeleton-color="skeletonColor"
    :shimmer-color="shimmerColor"
    :alt="fields.alt || undefined"
    class="image"
    v-bind="clickActionAttrs()"
    @click="emit('onClick')"
    @keydown="onActivationKeydown"
    @pointerenter="hovered = true"
    @pointerleave="clearInteractionState"
    @pointerdown="pressed = true"
    @pointerup="pressed = false"
    @pointercancel="pressed = false"
    @focus="onFocus"
    @blur="clearInteractionState"
  />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import PlainImage from '../../generic/PlainImage.vue'
import { type ImageFieldData } from '@render/composables/useImagePreset'
import {
  getColorStyleData,
  getSpacingStyleData,
  getSizeStyleData,
} from '@/render/composables/useStyleData'
import { useDirection } from '@render/composables/useDirection'
import { useClickAction } from '@render/composables/useClickAction'
// Type-only import — erased at compile time, so the editor-only definition
// never enters a built-pipe bundle (see reference/06-component-metadata.md).
// `flipOnRtl` is opt-in: it horizontally mirrors the image (scaleX(-1)) under
// RTL for directional graphics (arrows, etc.); images never mirror otherwise.
import type { ImageFields as Model } from './definition'

const emit = defineEmits<{
  (e: 'onClick'): void
}>()

// An <img> carries no interactive semantics, so an author-wired onClick is
// invisible to assistive tech and unreachable by keyboard. Expose it as a
// button (and make it activatable) only while a processor is actually bound —
// a decorative image must not be announced as an inert control. The accessible
// name comes from the `alt` field, so a clickable image should always set one.
const { clickActionAttrs, onActivationKeydown } = useClickAction({
  activate: () => emit('onClick'),
})

const fields = defineModel<Model>('fields', {
  required: true,
  default: () => ({
    image: { type: 'image_field', id: '' } satisfies ImageFieldData,
  }),
})

const hovered = ref(false)
const pressed = ref(false)

function clearInteractionState() {
  hovered.value = false
  pressed.value = false
}

// There is no CSS hover state on this component to borrow for keyboard focus,
// so focus reuses the author's `hoverImage` — the same swap the pointer gets.
// Gated on `:focus-visible` (what the browser itself uses to decide whether to
// paint a focus ring) because a click also focuses a role=button element, and
// without the gate the hover image would latch on after a tap. An engine that
// doesn't know the selector throws on `matches` — degrade to no visual rather
// than to a latched one.
function onFocus(event: FocusEvent) {
  const el = event.target as HTMLElement | null
  try {
    if (el?.matches(':focus-visible')) hovered.value = true
  } catch {
    // no-op
  }
}

const activeImage = computed(() => {
  if (pressed.value && fields.value.downImage?.id) return fields.value.downImage
  if (hovered.value && fields.value.hoverImage?.id) return fields.value.hoverImage
  return fields.value.image
})

const skeletonColor = computed(
  () => getColorStyleData(fields.value.imageSkeletonBgColor) ?? 'transparent',
)
const shimmerColor = computed(
  () => getColorStyleData(fields.value.imageSkeletonShimmerColor) ?? 'transparent',
)

const computedWidth = computed(() => getSizeStyleData(fields.value.width, '100%'))
const computedHeight = computed(() => getSizeStyleData(fields.value.height, 'auto'))
const computedBorderRadius = computed(() => getSizeStyleData(fields.value.borderRadius, '0'))
const computedMargin = computed(() => getSpacingStyleData(fields.value.margin, '0'))
const computedObjectFit = computed(() => fields.value.objectFit || 'contain')

const { isRtl } = useDirection()
const computedTransform = computed(() =>
  fields.value.flipOnRtl === true && isRtl.value ? 'scaleX(-1)' : 'none',
)
</script>

<style scoped>
.image {
  width: v-bind(computedWidth);
  height: v-bind(computedHeight);
  margin: v-bind(computedMargin);
  border-radius: v-bind(computedBorderRadius);
  object-fit: v-bind(computedObjectFit);
  transform: v-bind(computedTransform);
  display: block;
  overflow: hidden;
}
</style>
